/**
 * SC-972 — changer d'onglet en glissant le doigt, dans le carnet du serveur.
 *
 * Le geste est reconnu À LA FIN, jamais pendant : rien ne bouge sous le doigt,
 * donc rien n'est rendu à chaque pixel. La version « les trois onglets côte à
 * côte qu'on translate » a été écartée exprès — elle obligerait à monter Sur
 * place, Emporter et Résas en même temps, soit trois fois le rendu et trois
 * fois les sondages sur un téléphone de salle.
 *
 * Tout le sujet est de ne PAS voler un geste qui appartient à quelqu'un
 * d'autre : les barres de pastilles défilent horizontalement, et les listes
 * défilent verticalement.
 */

/** En deçà, c'est une hésitation ou un appui, pas un geste. 60px demandait un
 *  trop grand geste au pouce en salle ; un appui ne dérive jamais de 40px. */
export const SWIPE_MIN_DISTANCE_PX = 40;

/** Il faut que l'horizontal domine, sinon c'est un défilement. 1,5 et non 2 : le
 *  pouce décrit un arc, et à 2 un glissement réel mais penché était refusé. */
export const SWIPE_HORIZONTAL_RATIO = 1.5;

/** Un glissement qui dure est une sélection ou un défilement lent. */
export const SWIPE_MAX_DURATION_MS = 800;

export type SwipeDirection = 1 | -1 | 0;

/**
 * `1` = vers l'onglet suivant (doigt vers la gauche), `-1` = vers le
 * précédent, `0` = ce n'était pas un changement d'onglet.
 */
export function swipeDirection(dx: number, dy: number, durationMs: number): SwipeDirection {
  if (durationMs > SWIPE_MAX_DURATION_MS) return 0;
  if (Math.abs(dx) < SWIPE_MIN_DISTANCE_PX) return 0;
  if (Math.abs(dx) < Math.abs(dy) * SWIPE_HORIZONTAL_RATIO) return 0;
  return dx < 0 ? 1 : -1;
}

const SCROLLER_CLASSES = ['overflow-x-auto', 'overflow-x-scroll'];

/**
 * Le geste part-il d'une zone qui défile déjà horizontalement (barre de
 * pastilles, grille de tables) ? Alors il ne nous appartient pas.
 *
 * La classe est lue AUSSI BIEN que le débordement réel : en production une
 * classe peut disparaître d'un refactor. On mesure `scrollWidth >
 * clientWidth` plutôt que `getComputedStyle().overflowX`, pour deux raisons :
 * le style calculé retombe dans le piège DF-0229 sur tout ancêtre qui défile
 * verticalement à l'intérieur du panneau, et il n'est pas testable — jsdom ne
 * charge aucune feuille de style, donc un test vert ne disait rien de ce
 * chemin. Un conteneur qui ne déborde pas n'a de toute façon aucun geste à
 * défendre, quel que soit son `overflow-x`.
 *
 * DF-0229 — la frontière n'est JAMAIS examinée : c'est la surface du geste, pas
 * un concurrent. Elle porte `overflow-y: auto` (elle défile verticalement) et
 * la spécification CSS fait alors passer la valeur *calculée* de `overflow-x`
 * de `visible` à `auto`, sans qu'aucune règle ne l'écrive. Tant qu'on la
 * testait, elle se déclarait défileur horizontal et écartait **100 %** des
 * gestes, sur tous les onglets — mesuré en prod le 2026-09-13.
 */
export function startsInHorizontalScroller(
  target: Element | null,
  boundary: Element | null,
): boolean {
  let node: Element | null = target;
  while (node) {
    // Avant tout examen : la frontiere est a nous (cf. DF-0229 ci-dessus).
    if (node === boundary) return false;
    for (const cls of SCROLLER_CLASSES) {
      if (node.classList?.contains(cls)) return true;
    }
    if (node.scrollWidth > node.clientWidth) return true;
    node = node.parentElement;
  }
  return false;
}

/**
 * L'onglet voisin, ou `null` s'il n'y en a pas : le geste ne boucle pas. Un
 * serveur qui glisse deux fois de trop doit se retrouver au bout, pas revenu
 * au début sans l'avoir voulu.
 */
export function nextTab<T>(tabs: readonly T[], current: T, direction: SwipeDirection): T | null {
  if (direction === 0) return null;
  const index = tabs.indexOf(current);
  if (index < 0) return null;
  const target = index + direction;
  if (target < 0 || target >= tabs.length) return null;
  return tabs[target];
}
